import React, { useMemo, useRef } from 'react'

import { AgGridReact } from 'ag-grid-react'
import clsx from 'clsx'
import { LoadingOverlay } from 'components/commonparts/index'
import { useGridBase } from 'hooks/useGridBase'
import { dateToStr24HPad0DayOfWeek } from 'utils/convertFromLocalDate'
import 'utils/mst.scss'

import type { ColDef, GridReadyEvent } from 'ag-grid-community'
import type { InfoMessageList } from 'api-client'

interface DataListProps {
  list: InfoMessageList
  isLoading: boolean
}

function InfoMessageDataList({ list, isLoading }: DataListProps) {
  const gridRef = useRef<AgGridReact>(null)
  const containerStyle = useMemo(() => ({ width: '100%', height: '200px' }), [])
  const {
    localeText,
    defaultColDef: baseDefaultColDef,
    dateTimeFormatter,
  } = useGridBase({
    gridRef,
    isLoading,
  })

  const gridReady = useRef(false)
  const defaultColDef = useMemo(() => ({
    ...baseDefaultColDef,
    resizable: false,
    sortable: false,
    filter: false,
    autoHeaderHeight: false,
    suppressHeaderMenuButton: true,
  }), [baseDefaultColDef])

  const onGridReady = ((event: GridReadyEvent<any, any>) => {
    gridReady.current = true

    event.api.updateGridOptions({
      defaultColDef,
      headerHeight: 0,
      rowNumbers: true,
      cellSelection: {
        enableHeaderHighlight: true,
        handle: {
          mode: 'fill',
          direction: 'xy'
        }
      },
      animateRows: true,
      tooltipShowDelay: 0,
      tooltipHideDelay: 3000,
      rowSelection: {
        mode: 'singleRow',
        checkboxes: false,
        enableClickSelection: true,
    },
      rowDragManaged: true,
      pagination: false,
      rowHeight: 36,
      allowContextMenuWithControlKey: true,
      overlayNoRowsTemplate:
        '<span aria-live:"polite" aria-atomic:"true",pos>条件に合致する商品が見つかりません</span>',
      suppressLastEmptyLineOnPaste: true,
    })
  });

  const columnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: '',
      field: 'infoTime',
      width: 200,
      suppressSizeToFit: true,
      valueFormatter: dateTimeFormatter,
    },
    {
      headerName: '',
      field: 'targetName',
      width: 350,
      suppressSizeToFit: true,
    },
    {
      headerName: '',
      field: 'processTypeName',
      width: 200,
      suppressSizeToFit: true,
    },
    {
      headerName: '',
      field: 'infoTypeName',
      width: 150,
      suppressSizeToFit: true,
    },
    {
      headerName: '',
      field: 'message',
      flex: 1,
      minWidth: 400,
      suppressSizeToFit: false,
      cellClass: (params) =>
        // clsxを用いて、動的なクラス名を設定する
        clsx('super-app', {
          infotypeErrror: params.data?.infoType === 'error',
          infotypewarning: params.data?.infoType === 'warning',
        }),
      cellStyle: (params) => {
        if (params.data?.infoType === 'error') {
          return { color: 'firebrick' }
        }
        if (params.data?.infoType === 'warning') {
          return { color: 'darkorange' }
        }
        return null
      },
    },
  ], [])

  return (
    <div style={containerStyle} className="ag-theme-quartz">
      <AgGridReact
          ref={gridRef}
          rowData={list.list}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          localeText={localeText}
          loadingOverlayComponent={LoadingOverlay}
          getRowId={(params) => dateToStr24HPad0DayOfWeek(params.data.infoTime) + params.data.message}
          suppressRowHoverHighlight
          suppressPaginationPanel
          suppressScrollOnNewData
          rowHeight={40}
          domLayout="normal"
          suppressColumnVirtualisation
          suppressAutoSize
          skipHeaderOnAutoSize
          suppressColumnMoveAnimation
          suppressRowTransform
        />
    </div>
  )
}

export default InfoMessageDataList
